import _ from 'lodash';
import moment from 'moment';
import {
  get,
  post,
  put,
  del,
} from 'utils/fetch';
import { Storage, RequestState } from 'constants/constants';
import { mergeUrl } from 'utils/url';
import { clientTimezone } from 'utils/time';

import { selectApiHost, selectApiToken } from 'store/AppState';
import { pushTo, history } from 'store/RouteState';
import Overlay, { selectDefaultOverlay } from 'store/models/Overlay'
import { FETCH_RECENT_STREAMS } from 'store/models/Stream';


// ======================
// HELPERS
// ====================== 
export const mergeSceneStats = channels => {
  return _.reduce(channels, (stats, c) => {

    return _.mergeWith(stats, c.scenes, (o = {}, s = {}) => {
      let t = {
        shown: (o.shown || 0) + (s.shown || 0),
        minutesShown: (o.minutesShown || 0) + (s.minutesShown || 0),
        minutesViewed: (o.minutesViewed || 0) + (s.minutesViewed || 0),
      }
      return t;
    })
  }, {});
}

// ======================
// SELECTORS
// ====================== 
export const selectAccountStats = state => state.models.stats.account;
export const selectBrandStats = state => state.models.stats.brands;
export const selectChannelStats = state => state.models.stats.channels;
export const selectCampaignStats = state => state.models.stats.campaigns;

export const selectStatsByChannel = (state, channelId) => state.models.stats.byChannel[channelId];
export const selectChannelStreamStats = state => state.models.stats.channelStreams;
export const selectChannelLinkStats = state => state.models.stats.channelLinks;
export const selectChannelSceneStats = state => state.models.stats.channelScenes;
export const selectChannelGameStats = state => state.models.stats.channelGames;

export const selectStatsByCampaign = (state, campaignId) => state.models.stats.byCampaign[campaignId];
export const selectCampaignStreamStats = state => state.models.stats.campaignStreams;
export const selectCampaignLinkStats = state => state.models.stats.campaignLinks;
export const selectCampaignSceneStats = state => state.models.stats.campaignScenes;
export const selectCampaignGameStats = state => state.models.stats.campaignGames;

export const selectRecentStreamStats = state => state.models.stats.recentStreams;
export const selectStreamStats = (state, streamId) => state.models.stats.streams[streamId];
export const selectStreamGameStats = (state, streamId) => state.models.stats.streamGames[streamId];
export const selectStreamSceneStats = (state, streamId) => state.models.stats.streamScenes[streamId];
export const selectStreamLinkStats = (state, streamId) => state.models.stats.streamLinks[streamId];






// ======================
// ACTIONS
// ====================== 
export const FETCH_ACCOUNT_STATS = 'FETCH_ACCOUNT_STATS';
export const FETCH_CAMPAIGNS_STATS = 'FETCH_CAMPAIGNS_STATS';
export const FETCH_BRANDS_STATS = 'FETCH_BRANDS_STATS';
export const FETCH_CHANNELS_STATS = 'FETCH_CHANNELS_STATS';
export const FETCH_CHANNEL_DETAIL_STATS = 'FETCH_CHANNEL_DETAIL_STATS';
export const FETCH_CHANNEL_STREAM_STATS = 'FETCH_CHANNEL_STREAM_STATS';
export const FETCH_CHANNEL_SCENE_STATS = 'FETCH_CHANNEL_SCENE_STATS';
export const FETCH_CHANNEL_LINK_STATS = 'FETCH_CHANNEL_LINK_STATS';
export const FETCH_CHANNEL_GAME_STATS = 'FETCH_CHANNEL_GAME_STATS';
export const FETCH_CAMPAIGN_DETAIL_STATS = 'FETCH_CAMPAIGN_DETAIL_STATS';
export const FETCH_CAMPAIGN_STREAM_STATS = 'FETCH_CAMPAIGN_STREAM_STATS';
export const FETCH_CAMPAIGN_SCENE_STATS = 'FETCH_CAMPAIGN_SCENE_STATS';
export const FETCH_CAMPAIGN_LINK_STATS = 'FETCH_CAMPAIGN_LINK_STATS';
export const FETCH_CAMPAIGN_GAME_STATS = 'FETCH_CAMPAIGN_GAME_STATS';
export const FETCH_STREAM_STATS = 'FETCH_STREAM_STATS';
export const FETCH_STREAM_GAME_STATS = 'FETCH_STREAM_GAME_STATS';
export const FETCH_STREAM_SCENE_STATS = 'FETCH_STREAM_SCENE_STATS';
export const FETCH_STREAM_LINK_STATS = 'FETCH_STREAM_LINK_STATS';


// ======================
// MODEL
// ====================== 
const Model = {
  actions: {
    fetchAccountStats: (from, to, tag) => dispatch => get({
      url: "/api/stats/account",
      params: { from, to, tag },
      dispatch,
      action: FETCH_ACCOUNT_STATS,
    }),

    fetchBrandStats: (from, to) => dispatch => get({
      url: "/api/stats/brands",
      params: { from, to },
      dispatch,
      action: FETCH_BRANDS_STATS,
    }),

    fetchCampaignStats: (from, to) => dispatch => get({
      url: "/api/stats/campaigns",
      params: { from, to },
      dispatch,
      action: FETCH_CAMPAIGNS_STATS,
    }),

    fetchChannelStats: (from, to, tag) => dispatch => get({
      url: "/api/stats/channels",
      params: { from, to, tag },
      dispatch,
      action: FETCH_CHANNELS_STATS,
    }),

    fetchChannelDetailStats: (channelId, from, to) => dispatch => get({
      url: "/api/stats/channel/:channelId",
      params: { channelId, from, to },
      dispatch,
      action: FETCH_CHANNEL_DETAIL_STATS,
    }),

    fetchChannelStreamStats: (channelId, from, to) => dispatch => get({
      url: "/api/stats/channel/:channelId/streams",
      params: { channelId, from, to },
      dispatch,
      action: FETCH_CHANNEL_STREAM_STATS,
    }),

    fetchChannelSceneStats: (channelId, from, to) => dispatch => get({
      url: "/api/stats/channel/:channelId/scenes",
      params: { channelId, from, to },
      dispatch,
      action: FETCH_CHANNEL_SCENE_STATS,
    }),

    fetchChannelLinkStats: (channelId, from, to) => dispatch => get({
      url: "/api/stats/channel/:channelId/links",
      params: { channelId, from, to },
      dispatch,
      action: FETCH_CHANNEL_LINK_STATS,
    }),

    fetchChannelGameStats: (channelId, from, to) => dispatch => get({
      url: "/api/stats/channel/:channelId/games",
      params: { channelId, from, to },
      dispatch,
      action: FETCH_CHANNEL_GAME_STATS,
    }),

    fetchCampaignDetailStats: (campaignId, from, to) => dispatch => get({
      url: "/api/stats/campaign/:campaignId",
      params: { campaignId, from, to },
      dispatch,
      action: FETCH_CAMPAIGN_DETAIL_STATS,
    }),

    fetchCampaignStreamStats: (campaignId, from, to) => dispatch => get({
      url: "/api/stats/campaign/:campaignId/streams",
      params: { campaignId, from, to },
      dispatch,
      action: FETCH_CAMPAIGN_STREAM_STATS,
    }),

    fetchCampaignSceneStats: (campaignId, from, to) => dispatch => get({
      url: "/api/stats/campaign/:campaignId/scenes",
      params: { campaignId, from, to },
      dispatch,
      action: FETCH_CAMPAIGN_SCENE_STATS,
    }),

    fetchCampaignLinkStats: (campaignId, from, to) => dispatch => get({
      url: "/api/stats/campaign/:campaignId/links",
      params: { campaignId, from, to },
      dispatch,
      action: FETCH_CAMPAIGN_LINK_STATS,
    }),

    fetchCampaignGameStats: (campaignId, from, to) => dispatch => get({
      url: "/api/stats/campaign/:campaignId/games",
      params: { campaignId, from, to },
      dispatch,
      action: FETCH_CAMPAIGN_GAME_STATS,
    }),

    fetchStreamStats: (streamId) => dispatch => get({
      url: "/api/stats/stream/:streamId",
      params: { streamId },
      dispatch,
      action: FETCH_STREAM_STATS,
    }),

    fetchStreamGameStats: (streamId) => dispatch => get({
      url: "/api/stats/stream/:streamId/games",
      params: { streamId },
      dispatch,
      action: FETCH_STREAM_GAME_STATS,
    }),

    fetchStreamSceneStats: (streamId) => dispatch => get({
      url: "/api/stats/stream/:streamId/scenes",
      params: { streamId },
      dispatch,
      action: FETCH_STREAM_SCENE_STATS,
    }),

    fetchStreamLinkStats: (streamId) => dispatch => get({
      url: "/api/stats/stream/:streamId/links",
      params: { streamId },
      dispatch,
      action: FETCH_STREAM_LINK_STATS,
    }),

    exportReport: (type, from, to) => async (dispatch, getState) => {
      from = moment(from).valueOf();
      to = moment(to).valueOf();
      let tz = clientTimezone();
      window.location = mergeUrl('/api/export/:type', { type, from, to, tz});
    },

    exportDetailsReport: (type, id, from, to) => async (dispatch, getState) => {
      from = moment(from).valueOf();
      to = moment(to).valueOf();
      let tz = clientTimezone();
      window.location = mergeUrl('/api/export/:type/:id', { type, id, from, to, tz});
    },

  },

  spec: {
    stats: {
      channels: {},
      campaigns: {},
      brands: {},
      account: {},
      byChannel: {},
      channelStreams: {},
      channelScenes: {},
      channelLinks: {},
      channelGames: {},
      byCampaign: {},
      campaignStreams: {},
      campaignScenes: {},
      campaignLinks: {},
      campaignGames: {},
      recentStreams: {},
      streams: {},
      streamGames: {},
      streamScenes: {},
      streamLinks: {},
    }
  },

  reducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    switch(type) {

      case FETCH_ACCOUNT_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            account: body.accountStats,
          }
        }

      case FETCH_BRANDS_STATS:
        return {
          ...state,
          stats: {
            ...state.stats,
            brands: {
              ...state.brands,
              ...body.brandStats,
            }
          }
        }

      case FETCH_CAMPAIGNS_STATS:
        return { ...state, 
          stats: {
            ...state.stats,
            campaigns: {
              ...state.campaigns,
              ...body.campaignStats,
            }
          }
        }

      case FETCH_CHANNELS_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            channels: {
              ...state.stats.channels,
              ...body.channelStats,
            }
          }
        }

      case FETCH_CHANNEL_DETAIL_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            byChannel: {
              ...state.stats.byChannel,
              [action.params.channelId]: {...body.channelStats },
            }
          }
        }

      case FETCH_CHANNEL_STREAM_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            channelStreams: {
              ...state.stats.channelStreams,
              ...body.streamStats,
            }
          }
        }
    
      case FETCH_CHANNEL_GAME_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            channelGames: {
              ...body.gameStats,
            }
          }
        }

      case FETCH_CHANNEL_SCENE_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            channelScenes: {
              ...body.sceneStats,
            }
          }
        }

      case FETCH_CHANNEL_LINK_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            channelLinks: {
              ...body.linkStats,
            }
          }
        }
      
      case FETCH_CAMPAIGN_DETAIL_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            byCampaign: {
              ...state.stats.byCampaign,
              [action.params.campaignId]: {...body.campaignStats },
            }
          }
        }

      case FETCH_CAMPAIGN_STREAM_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            campaignStreams: {
              ...state.stats.campaignStreams,
              ...body.streamStats,
            }
          }
        }

      case FETCH_CAMPAIGN_SCENE_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            campaignScenes: {
              ...state.stats.campaignScenes,
              ...body.sceneStats,
            }
          }
        }

      case FETCH_CAMPAIGN_LINK_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            campaignLinks: {
              ...state.stats.campaignLinks,
              ...body.linkStats,
            }
          }
        }

      case FETCH_CAMPAIGN_GAME_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            campaignGames: {
              ...state.stats.campaignGames,
              ...body.gameStats,
            }
          }
        }

      case FETCH_RECENT_STREAMS:
        return { ...state,
          stats: {
            ...state.stats,
            recentStreams: {
              ...state.stats.recentStreams,
              ...body.streamStats,
            }
          }
        }

      case FETCH_STREAM_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            streams: {
              ...state.stats.streams,
              ...body.streamStats,
            }
          }
        }

      case FETCH_STREAM_GAME_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            streamGames: {
              ...state.stats.streamGames,
              [action.params.streamId]: {...body.gameStats },
            }
          }
        }
      
      case FETCH_STREAM_SCENE_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            streamScenes: {
              ...state.stats.streamScenes,
              [action.params.streamId]: {...body.sceneStats },
            }
          }
        }

      case FETCH_STREAM_LINK_STATS:
        return { ...state,
          stats: {
            ...state.stats,
            streamLinks: {
              ...state.stats.streamLinks,
              [action.params.streamId]: {...body.linkStats },
            }
          }
        }

      default:
        return state;
    }

    return state;
  },
}
export default Model;
